import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import SectionHeading from "@/components/base/SectionHeading";
import { movies } from "@/mocks/movies";
import { actors } from "@/mocks/actors";
import { newsList } from "@/mocks/news";
import { useSeo } from "@/hooks/useSeo";
import type { Movie } from "@/types";

export default function MovieDetail() {
  const { id } = useParams<{ id: string }>();
  const [copied, setCopied] = useState(false);

  const movie: Movie | undefined = movies.find((item) => item.id === id);
  const cast = movie ? actors.filter((actor) => movie.cast.includes(actor.id)) : [];
  const related = movie
    ? movies
        .filter(
          (item) => item.id !== movie.id && item.genres.some((genre) => movie.genres.includes(genre)),
        )
        .slice(0, 4)
    : [];
  const relatedNews = movie
    ? newsList.filter((item) => item.relatedMovies.includes(movie.id)).slice(0, 3)
    : [];

  useSeo({
    title: movie
      ? `《${movie.title}》剧情介绍与演员资料 - 蜜桃成熟时电影`
      : "影片未找到 - 蜜桃成熟时电影",
    description: movie
      ? `${movie.title}（${movie.year}）：${movie.summary} 了解导演、主演、片长、类型标签与完整剧情介绍。`
      : "未找到对应的影片资料，请返回电影分类页重新浏览。",
    keywords: movie
      ? `${movie.title},${movie.title}剧情,${movie.title}演员,${movie.director},${movie.genres.join(",")}`
      : "蜜桃成熟时电影,电影资料",
  });

  useEffect(() => {
    setCopied(false);
  }, [id]);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2200);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  if (!movie) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full max-w-[1240px] flex-col items-center gap-4 px-4 py-24 text-center md:px-6">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-400">
            <i className="ri-file-warning-line text-2xl" />
          </span>
          <h1 className="font-heading text-2xl font-semibold text-foreground-950">未找到这部影片</h1>
          <p className="max-w-md text-sm leading-relaxed text-foreground-600">
            影片可能已调整或链接有误。你可以回到电影分类页，浏览系列完整片目。
          </p>
          <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
            <Link
              to="/category"
              className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              浏览电影分类
            </Link>
            <Link
              to="/"
              className="whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-400"
            >
              返回首页
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full max-w-[1240px] px-4 py-4 md:px-6">
          <Breadcrumb
            items={[{ label: "首页", to: "/" }, { label: "电影分类", to: "/category" }, { label: movie.title }]}
          />
        </div>
      </div>

      <section className="relative w-full overflow-hidden bg-foreground-950">
        <img
          src={movie.backdrop}
          alt={`${movie.title}剧照`}
          title={`${movie.title}剧情介绍`}
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/95 via-foreground-950/75 to-foreground-950/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/20 to-foreground-950/40" />

        <div className="relative mx-auto flex w-full max-w-[1240px] flex-col gap-6 px-4 py-10 md:flex-row md:items-end md:gap-10 md:px-6 md:py-14">
          <div className="w-[132px] shrink-0 overflow-hidden rounded-lg border border-background-50/20 bg-background-200 md:w-[220px]">
            <img
              src={movie.cover}
              alt={`${movie.title}电影封面`}
              title={`${movie.title}高清封面`}
              className="aspect-[2/3] w-full object-cover object-top"
            />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary-500 px-2.5 py-1 text-[11px] font-medium text-background-50">
                系列作品
              </span>
              {movie.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/category?tag=${encodeURIComponent(tag)}`}
                  className="rounded-full border border-background-50/25 px-2.5 py-1 text-[11px] text-background-100/85 transition-colors hover:border-background-50/60"
                >
                  {tag}
                </Link>
              ))}
            </div>

            <h1 className="mt-3 font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
              {movie.title}
            </h1>
            <p className="mt-2 text-[11px] uppercase tracking-[0.24em] text-background-200/70">
              {movie.englishTitle} · {movie.year}
            </p>

            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-background-100/85">
              <span className="flex items-center gap-1.5 font-medium text-primary-200">
                <i className="ri-star-fill" />
                {movie.rating.toFixed(1)}
              </span>
              <span className="h-3 w-px bg-background-50/25" />
              <span>{movie.genres.join(" / ")}</span>
              <span className="h-3 w-px bg-background-50/25" />
              <span>{movie.duration} 分钟</span>
              <span className="h-3 w-px bg-background-50/25" />
              <span>导演 {movie.director}</span>
            </div>

            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-100/90">{movie.summary}</p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className={copied ? "ri-check-line" : "ri-share-forward-line"} />
                {copied ? "链接已复制" : "复制分享链接"}
              </button>
              <Link
                to="/category"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/35 px-4 py-2.5 text-sm text-background-50 transition-colors hover:bg-background-50/10"
              >
                <i className="ri-arrow-left-line" />
                返回电影分类
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto grid w-full max-w-[1240px] gap-10 px-4 py-10 md:px-6 md:py-14 lg:grid-cols-[1.6fr_1fr] lg:gap-12">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 font-heading text-xl font-semibold text-foreground-950">
            <span className="h-4 w-1 rounded-full bg-accent-500" />
            剧情介绍
          </h2>
          <div className="mt-4 space-y-4">
            {movie.plot.map((paragraph, index) => (
              <p key={index} className="text-sm leading-[1.95] text-foreground-700 md:text-[15px]">
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-8 rounded-lg bg-background-100 p-5">
            <h3 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-950">
              <i className="ri-sparkling-line text-accent-500" />
              本片看点
            </h3>
            <ul className="mt-3 grid gap-2 sm:grid-cols-2">
              {movie.highlights.map((highlight) => (
                <li key={highlight} className="flex items-start gap-2 text-xs leading-relaxed text-foreground-600">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500" />
                  {highlight}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-10">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-heading text-xl font-semibold text-foreground-950">
                <span className="h-4 w-1 rounded-full bg-accent-500" />
                演职员
              </h2>
              <Link to="/actors" className="text-xs text-foreground-500 transition-colors hover:text-accent-600">
                全部演员资料
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {cast.map((actor) => (
                <Link
                  key={actor.id}
                  to={`/actors/${actor.id}`}
                  className="group flex items-center gap-3 rounded-lg border border-background-200/80 bg-background-50 p-3 transition-colors hover:border-primary-300"
                >
                  <span className="h-12 w-12 shrink-0 overflow-hidden rounded-full bg-background-200">
                    <img
                      src={actor.avatar}
                      alt={`${actor.name}头像`}
                      title={`${actor.name}演员资料`}
                      className="h-full w-full object-cover object-top"
                    />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-foreground-900 transition-colors group-hover:text-accent-600">
                      {actor.name}
                    </span>
                    <span className="block truncate text-[11px] text-foreground-400">{actor.englishName}</span>
                  </span>
                </Link>
              ))}
            </div>
          </div>

          {relatedNews.length > 0 && (
            <div className="mt-10">
              <h2 className="flex items-center gap-2 font-heading text-xl font-semibold text-foreground-950">
                <span className="h-4 w-1 rounded-full bg-accent-500" />
                相关资讯
              </h2>
              <div className="mt-4 flex flex-col divide-y divide-background-200 border-y border-background-200">
                {relatedNews.map((item) => (
                  <Link
                    key={item.id}
                    to={`/news/${item.id}`}
                    className="group flex items-center justify-between gap-4 py-3.5"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-foreground-800 transition-colors group-hover:text-accent-600">
                        {item.title}
                      </span>
                      <span className="mt-1 block text-[11px] text-foreground-400">
                        {item.category} · {item.date}
                      </span>
                    </span>
                    <i className="ri-arrow-right-up-line shrink-0 text-foreground-300 transition-colors group-hover:text-accent-500" />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        <aside className="min-w-0">
          <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
            <h2 className="font-heading text-sm font-semibold text-foreground-950">影片资料</h2>
            <dl className="mt-4 space-y-3 text-xs">
              {[
                { label: "上映年份", value: String(movie.year) },
                { label: "片长", value: `${movie.duration} 分钟` },
                { label: "地区", value: movie.region },
                { label: "导演", value: movie.director },
                { label: "类型", value: movie.genres.join(" / ") },
                { label: "主演", value: cast.map((actor) => actor.name).join("、") || "—" },
                { label: "评分", value: `${movie.rating.toFixed(1)} / 10` },
              ].map((row) => (
                <div key={row.label} className="flex items-start justify-between gap-4 border-b border-background-200/70 pb-3 last:border-0 last:pb-0">
                  <dt className="shrink-0 text-foreground-400">{row.label}</dt>
                  <dd className="text-right leading-relaxed text-foreground-800">{row.value}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="mt-4 rounded-lg bg-secondary-100 p-5">
            <h2 className="font-heading text-sm font-semibold text-foreground-950">类型与标签</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {movie.genres.map((genre) => (
                <Link
                  key={genre}
                  to={`/category?genre=${encodeURIComponent(genre)}`}
                  className="rounded-full bg-background-50 px-3 py-1.5 text-[11px] text-foreground-700 transition-colors hover:text-accent-600"
                >
                  #{genre}
                </Link>
              ))}
              {movie.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/category?tag=${encodeURIComponent(tag)}`}
                  className="rounded-full bg-accent-100 px-3 py-1.5 text-[11px] text-accent-800 transition-colors hover:bg-accent-200"
                >
                  #{tag}
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-4 rounded-lg border border-dashed border-background-300 p-5">
            <p className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.14em] text-foreground-400">
              <i className="ri-information-line" />
              演示说明
            </p>
            <p className="mt-2 text-xs leading-relaxed text-foreground-500">
              本站为影视资料演示站点，影片条目与剧情内容均为模拟数据，不提供在线播放、下载或票务服务。
            </p>
          </div>
        </aside>
      </div>

      {related.length > 0 && (
        <section className="mx-auto w-full max-w-[1240px] px-4 pb-4 md:px-6">
          <SectionHeading
            eyebrow="You May Also Like"
            title="相关推荐"
            description="根据类型标签为你挑选的系列作品。"
            actionText="更多影片"
            actionTo="/category"
          />
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-5">
            {related.map((item) => (
              <MovieCard key={item.id} movie={item} showSummary={false} />
            ))}
          </div>
        </section>
      )}
    </SiteLayout>
  );
}